import { __ } from '@wordpress/i18n'

class VideoBlock {
  element: HTMLElement
  playing = false
  autoHideTimeout?: number
  autoHidden = false
  autoHideDelay = 2000

  constructor(element: HTMLElement) {
    this.element = element
    this.onToggleClick = this.onToggleClick.bind(this)
    this.play = this.play.bind(this)
    this.pause = this.pause.bind(this)
    this.onVideoPlay = this.onVideoPlay.bind(this)
    this.onVideoPause = this.onVideoPause.bind(this)
    this.resetAutoHide = this.resetAutoHide.bind(this)
    this.onFullscreenChange = this.onFullscreenChange.bind(this)
    this.toggleElement?.addEventListener('click', this.onToggleClick)
    this.videoElement?.addEventListener('pointerdown', this.onPointerDown)
    this.videoElement?.addEventListener('playing', this.onVideoPlay)
    this.videoElement?.addEventListener('pause', this.onVideoPause)
    this.videoElement?.addEventListener(
      'webkitfullscreenchange',
      this.onFullscreenChange
    )
    this.videoElement?.addEventListener(
      'fullscreenchange',
      this.onFullscreenChange
    )
    this.element.addEventListener('mousemove', () => {
      if (this.autoHidden) {
        this.resetAutoHide()
      }
    })
    this.toggleElement?.addEventListener('focus', () => this.setAutoHide(false))
    this.toggleElement?.addEventListener('blur', () => this.resetAutoHide())
    this.videoElement?.addEventListener('loadeddata', this.play)
    this.onVideoPause()
  }

  private get videoElement() {
    return this.element?.querySelector<HTMLVideoElement>('video')
  }

  private get toggleElement() {
    return this.element?.querySelector<HTMLElement>('.video-toggle')
  }

  onFullscreenChange(evt: Event) {
    const isFullscreenNow =
      (document.webkitFullscreenElement || document.fullscreenElement) !== null
    this.element.classList.toggle('is-fullscreen', isFullscreenNow)
  }

  // this is needed to allow sliding the video in a slider
  onPointerDown(evt: PointerEvent) {
    evt.preventDefault()
  }

  onToggleClick() {
    if (!this.playing) {
      this.play()
    } else {
      this.pause()
    }
  }

  onVideoPlay() {
    this.playing = true
    this.element.classList.toggle('is-playing', true)
    this.toggleElement?.setAttribute('aria-label', __('pause video', 'icf'))
    this.resetAutoHide()
  }

  onVideoPause() {
    this.playing = false
    this.element.classList.toggle('is-playing', false)
    this.toggleElement?.setAttribute('aria-label', __('play video', 'icf'))
    this.setAutoHide(false)
  }

  play() {
    const video = this.videoElement
    if (!video) return
    if (video.paused || video.ended) {
      video.play()
    }
  }

  pause() {
    const video = this.videoElement
    if (!video) return
    if (!video.paused) {
      video.pause()
    }
  }

  resetAutoHide() {
    this.setAutoHide(false)
    clearTimeout(this.autoHideTimeout)
    this.autoHideTimeout = window.setTimeout(
      () => this.setAutoHide(true),
      this.autoHideDelay
    )
  }

  setAutoHide(state: boolean) {
    clearTimeout(this.autoHideTimeout)
    this.autoHidden = !!state
    this.element.classList.toggle('autohide', state)
    this.element.style.cursor = state ? 'none' : ''
  }
}

for (const videoBlockElem of document.querySelectorAll<HTMLElement>(
  '.wp-block-video'
)) {
  new VideoBlock(videoBlockElem)
}
